import type {
  EnvironmentId,
  ProjectCheckoutDeletionPreview,
  ProjectId,
  ScopedProjectRef,
} from "@cmmd-forge/contracts";
import { scopeProjectRef, scopedProjectKey } from "@cmmd-forge/client-runtime";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
// Straight from the module that OWNS the catalog, not through a re-export hop:
// `locate()` is the single source of every delete target, and an indirection
// that hides which module that is has already cost one round of confusion.
import type {
  ClickedCloudRowIdentity,
  CloudProjectCatalog,
  CloudRemovalTarget,
} from "~/environments/primary/cloudProjectCatalog";
import {
  resolveCloudRemovalRoutes,
  type CloudRemovalRoute,
  type RemovableForgeProject,
} from "./sidebarCloudRemovalRoutes";
import {
  removeProjectMembers,
  resolveLocalProjectMemberRefs,
  resolveRuntimeHostedProjectMemberRefs,
} from "./sidebarProjectRemovalMembers";
import { refreshCmmdForgeRuntimeList } from "../../cmmd/cmmdForgeRuntimeListResource";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";
import { clearCloudRuntimeProjectHandoff } from "./cloudRuntimeProjectHandoffStore";
import { REMOVAL_STEP_LABELS, type RemovalStepLabel } from "./removalStepLabels";
import { destroyCmmdRuntimeForEnvironment, settleRemovalStep } from "./sidebarProjectRemovalSteps";

// Re-exported so the removal module stays the one public door onto removal,
// which is what its callers already import.
export { destroyCmmdRuntimeForEnvironment };
import {
  createCmmdControlPlaneClient,
  readDesktopCloudBootstrap,
} from "./sidebarProjectRemovalControlPlaneClient";
import {
  removeCloudRegistrationWithSaga,
  withRemoveProjectOperationTimeout,
} from "./sidebarCloudProjectRemovalSaga";

export type { RemovableForgeProject };

function removalProjectRef(target: {
  readonly environmentId: string;
  readonly projectId: string;
}): ScopedProjectRef {
  return scopeProjectRef(target.environmentId as EnvironmentId, target.projectId as ProjectId);
}

export function createRemovableForgeProject(
  targets: readonly {
    readonly environmentId: string;
    readonly projectId: string;
  }[],
): RemovableForgeProject {
  const [target, ...remainingTargets] = targets;
  if (!target) throw new Error("No Forge execution project is connected.");
  const projectRef = removalProjectRef(target);
  return {
    environmentId: projectRef.environmentId,
    id: projectRef.projectId,
    memberProjectRefs: [projectRef, ...remainingTargets.map(removalProjectRef)],
    worktreeMembers: [],
    preConnectCloud: null,
    cloudRegistrationProjectId: null,
  };
}

function dedupeProjectRefs(refs: readonly ScopedProjectRef[]): ScopedProjectRef[] {
  const seen = new Set<string>();
  const out: ScopedProjectRef[] = [];
  for (const ref of refs) {
    const key = scopedProjectKey(ref);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(ref);
  }
  return out;
}

export interface SidebarRemovalProjectIds {
  /** CMMD PLANNING project, for the opt-in "delete in CMMD as well" cascade. */
  readonly cmmdProjectId: number | null;
  /**
   * What this row believes its Forge Cloud REGISTRATION to be.
   *
   * DISPLAY/PLUMBING ONLY — it names nothing destructive. It is passed on as
   * `RemoveSidebarProjectOptions.cloudRegistrationProjectId`, which removal
   * accepts and ignores; the registration a DELETE may name comes from the live
   * catalog read, never from here. That is why the `??` chain below is not on
   * the target-resolution path.
   */
  readonly cloudRegistrationProjectId: number | null;
}

/**
 * Split the two CMMD ids a sidebar row can carry. They are different things and
 * conflating them is destructive: a LOCAL row's `cwdLinkedCmmdProjectId` is a
 * cwd-based CMMD *planning* project (`useCmmdProjectLink`), and unregistering
 * it as though it were a Forge Cloud registration deletes an unrelated cloud
 * project the user never asked to lose.
 *
 * The PLANNING id it returns really is used — the opt-in cascade acts on it.
 * The registration id is inert (see above): removal derives the row's identity
 * itself and has the live catalog name the target.
 */
export function resolveSidebarRemovalProjectIds(
  project: SidebarProjectSnapshot,
  cwdLinkedCmmdProjectId: number | null | undefined,
): SidebarRemovalProjectIds {
  const cloudRegistrationProjectId =
    project.preConnectCloud?.cmmdProjectId ?? project.cloudRegistrationProjectId ?? null;
  return {
    cmmdProjectId: cloudRegistrationProjectId ?? cwdLinkedCmmdProjectId ?? null,
    cloudRegistrationProjectId,
  };
}

/**
 * The LIVE catalog — the same `/api/cmmd/forge-cloud-projects` list the
 * sidebar's cloud group renders, read through the same client on both surfaces.
 *
 * This is the one call in `CmmdForgeRuntimeApiClient` that is already
 * surface-aware in BOTH directions: the browser uses its same-origin proxy,
 * while packaged desktop uses the mounted local proxy with its owner-session
 * bearer. That is why removal both resolves ids from it and proves outcomes
 * against it — unlike a mutation response whose body may differ across legs.
 *
 * Rejects when the catalog could not be read. Callers must treat that as
 * unknown, never as "there is nothing there".
 *
 * Returns the `CloudProjectCatalog` value, NOT its rows. Every question this
 * module asks of the catalog goes through `catalog.locate()`, which cannot
 * answer "absent" unless absence is provable — see `cloudProjectCatalog.ts`.
 * Handing back a bare array is what let each previous instance of this bug
 * re-appear somewhere new: an array invites `.some(...)`, `.find(...)` and
 * `?? []`, and every one of those reads "no match" as "not there".
 */
async function readLiveCloudProjectCatalog(signal: AbortSignal): Promise<CloudProjectCatalog> {
  const bootstrap = readDesktopCloudBootstrap();
  return createCmmdControlPlaneClient(bootstrap).listCloudProjects({ signal });
}

/**
 * Prove the project is GONE by re-reading the catalog the sidebar renders.
 *
 * This is the authoritative outcome check, and it is a READ rather than a field
 * on the DELETE response on purpose. CMMD's unregister answers ok whether or
 * not it matched a row, and its `{ok, deleted}` body is discarded by the Forge
 * proxy on the browser leg — so "did this work?" cannot be answered from the
 * DELETE there at all. The catalog GET is the same call on both surfaces, so
 * the answer no longer depends on which transport carried the delete.
 *
 * The question asked is deliberately "is this row gone?", not "how many rows
 * did the delete touch?": a count cannot tell a real delete from one that
 * matched nothing.
 *
 * It asks about the TARGET — the row the live catalog named when resolution
 * decided what to delete — and about nothing else. The identity comes from the
 * target itself (`asConfirmationIdentity`), so the id that was deleted and the
 * id that is checked are the same value by construction; there is no second
 * expression that could drift, and no runtime id to widen the match into a
 * false "still listed" if CMMD's overlay has since moved (#66).
 *
 * Throws in both failure directions, which mean different things:
 *  - still listed -> the removal removed nothing. The row is still on screen;
 *    that must never read as success.
 *  - read failed  -> unproven, which is not the same as disproven. Unregister
 *    is idempotent, so the honest message is safe to retry and the retry
 *    confirms.
 *
 * A registration id decides every catalog row, which is what entitles this step
 * to read "no match" as proof of absence. `locate` still hands back
 * `unprovable` for identities that cannot exhaust the catalog, and that branch
 * is written out below even though a target can never produce it.
 */
interface CloudRegistrationRemoval {
  readonly route: CloudRemovalRoute;
  readonly target: CloudRemovalTarget;
}

async function confirmSidebarProjectLeftCloudCatalog(
  registrations: readonly CloudRegistrationRemoval[],
): Promise<void> {
  const route = registrations[0]?.route;
  if (!route) return;
  const confirmation = await settleRemovalStep(REMOVAL_STEP_LABELS.confirm, (signal) =>
    readLiveCloudProjectCatalog(signal),
  );

  if (!confirmation.ok) {
    throw new Error(
      "Forge Cloud could not confirm this project was removed. Please retry — removing it again is safe.",
      { cause: confirmation.error },
    );
  }

  const freshCatalog: CloudProjectCatalog = confirmation.value;
  for (const { target } of registrations) {
    freshCatalog.locate(target.asConfirmationIdentity()).match<void>({
      provablyAbsent: () => undefined,
      present: () => {
        throw new Error(
          "Forge Cloud did not remove this project — one or more registrations are still listed in the Forge Cloud project catalog.",
        );
      },
      // Unreachable: a target always carries a registration id, which decides
      // every catalog row. Written out anyway — the compiler demands it, and the
      // day someone widens the callers this branch is what stops "I could not
      // tell" from being reported to the user as "removed".
      unprovable: () => {
        throw new Error(
          "Forge Cloud could not confirm this project was removed. Please retry — removing it again is safe.",
        );
      },
    });
  }
}

/**
 * What a removal is allowed to do, decided ONCE.
 *
 * Three outcomes, one of which carries a delete target and none of which is a
 * nullable id. That shape is the fix: the previous design returned
 * `cmmdProjectId: number | undefined` alongside a separate "could not identify"
 * flag, and every instance of this bug was some caller recombining those parts
 * — `claimed ?? proven`, `undefined` silently meaning "take the runtime path".
 * There is nothing here to recombine. `match` is the only member, so a caller
 * cannot look at a target without also writing what it does when there is none.
 */
interface CloudRemovalPlan {
  readonly match: <T>(handlers: {
    /** The live catalog named these registrations for the row that was clicked. */
    readonly deleteRegistrations: (registrations: readonly CloudRegistrationRemoval[]) => T;
    /**
     * This row has no Forge Cloud registration to delete: a plain local row
     * without cloud members, or a cloud-capable row the catalog PROVABLY does
     * not list. Never a row whose entry merely could not be seen — that is
     * `refuse`.
     */
    readonly noCloudRegistration: () => T;
    /** The catalog could not name this row. Nothing destructive may run. */
    readonly refuse: (reason: string) => T;
  }) => T;
}

function planDeleteRegistrations(
  registrations: readonly CloudRegistrationRemoval[],
): CloudRemovalPlan {
  return { match: (handlers) => handlers.deleteRegistrations(registrations) };
}

function planNoCloudRegistration(): CloudRemovalPlan {
  return { match: (handlers) => handlers.noCloudRegistration() };
}

function planRefuse(reason: string): CloudRemovalPlan {
  return { match: (handlers) => handlers.refuse(reason) };
}

/**
 * Whether resolving this row's plan will actually hit the live catalog.
 *
 * Exists so the resolver and the PROGRESS REPORTING agree by construction. The
 * reporter must not announce "Reading the Forge Cloud project list" for a local
 * row that never reads it — a progress line describing work that is not
 * happening is the same class of lie as a silent removal, just in the other
 * direction.
 *
 * It is a predicate rather than a callback parameter on `resolveCloudRemovalPlan`
 * on purpose: that resolver takes the row and NOTHING ELSE (see its doc block),
 * and every instance of the delete-the-wrong-project bug entered through a
 * parameter someone added to it. A pure predicate cannot be `??`'d into a
 * DELETE, and it keeps ONE copy of the "is there anything to ask the catalog"
 * decision — the resolver is its only other reader.
 *
 * False for a plain local/desktop row without cloud members, and for a cloud
 * row with neither a registration of its own nor a locator: such a row can
 * name nothing to the catalog, so there is nothing to ask and nothing to
 * delete.
 */
function removalReadsCloudCatalog(project: RemovableForgeProject): boolean {
  const routes = resolveCloudRemovalRoutes(project);
  return (
    typeof project.preConnectCloud?.cmmdProjectId === "number" ||
    typeof project.cloudRegistrationProjectId === "number" ||
    routes.some((route) => routeLocatorIdentity(route) !== null)
  );
}

/**
 * How a route names itself to the catalog, or null when it cannot.
 *
 * One locator, never both: the two identities answer the same question and
 * offering a pair would put a choice between two candidate answers back into
 * this module, which is the shape every instance of its bug had. The runtime id
 * wins where a row has one, because that is the identity the catalog's own
 * `runtime.runtimeId` column is keyed by.
 */
function routeLocatorIdentity(route: CloudRemovalRoute): ClickedCloudRowIdentity | null {
  if (route.runtimeId !== null) return { runtimeId: route.runtimeId };
  return route.cmmdEnvironmentId === null ? null : { cmmdEnvironmentId: route.cmmdEnvironmentId };
}

type RouteLookup =
  | {
      readonly kind: "present";
      readonly registration: CloudRegistrationRemoval;
    }
  | { readonly kind: "absent" }
  | { readonly kind: "refuse"; readonly reason: string };

function lookupCloudRegistration(
  catalog: CloudProjectCatalog,
  route: CloudRemovalRoute,
  identity: ClickedCloudRowIdentity,
): RouteLookup {
  return catalog.locate(identity).match<RouteLookup>({
    present: ({ target }) => ({
      kind: "present",
      registration: { target, route },
    }),
    provablyAbsent: () => ({ kind: "absent" }),
    unprovable: (reason) => ({ kind: "refuse", reason }),
  });
}

function lookupCloudRemovalRoutes(
  catalog: CloudProjectCatalog,
  routes: readonly CloudRemovalRoute[],
  ownRegistrationProjectId: number | undefined,
): readonly RouteLookup[] {
  const catalogRoute = routes[0];
  if (!catalogRoute) return [];
  const ownRegistrationLookup =
    ownRegistrationProjectId === undefined
      ? []
      : [
          lookupCloudRegistration(catalog, catalogRoute, {
            registrationProjectId: ownRegistrationProjectId,
          }),
        ];
  const locatorLookups = routes.flatMap((route, index) => {
    if (index === 0 && ownRegistrationProjectId !== undefined) return [];
    const identity = routeLocatorIdentity(route);
    return identity === null ? [] : [lookupCloudRegistration(catalog, route, identity)];
  });
  return [...ownRegistrationLookup, ...locatorLookups];
}

function planCloudRemovalLookups(lookups: readonly RouteLookup[]): CloudRemovalPlan {
  const refusal = lookups.find((lookup) => lookup.kind === "refuse");
  if (refusal?.kind === "refuse") return planRefuse(refusal.reason);

  const registrations = [
    ...new Map(
      lookups.flatMap((lookup) =>
        lookup.kind === "present"
          ? [[lookup.registration.target.registrationProjectId, lookup.registration] as const]
          : [],
      ),
    ).values(),
  ];
  return registrations.length > 0
    ? planDeleteRegistrations(registrations)
    : planNoCloudRegistration();
}

/**
 * THE resolver. The only function in this module that decides what a removal
 * may remove, and the only path by which a `CloudRemovalTarget` comes into
 * existence for the sidebar.
 *
 * It takes the sidebar row and NOTHING ELSE — no options, no caller ids, no
 * cached snapshot. That is not tidiness: over six review cycles every single
 * instance of this bug entered through one of those inputs, and a parameter
 * that does not exist cannot be `??`'d into a destructive saga request.
 *
 * How the row names itself, and why only these identities exist:
 *  - a PRE-CONNECT row IS a catalog registration — it exists on screen only as
 *    a rendering of one — so it offers `registrationProjectId`. That decides
 *    every catalog row, so this row can be proved absent as well as present;
 *  - a CONNECTED row offers the locator it is addressed by, either from its
 *    representative or a cloud member on a mixed row: the `runtimeId` it is
 *    talking to, or, for a durable Environment whose id encodes no runtime, the
 *    Environment id itself. Both live on a catalog row's runtime overlay, so
 *    either can match a row and neither can rule one out (see
 *    `ClickedCloudRowIdentity`) — an unmatched connected row is `unprovable`,
 *    never "gone". A row offers ONE of them, never a pair to choose between.
 *
 * In BOTH cases the live catalog is read and the removal target is minted from
 * the row that read matched. There is no cheap path that skips the read for a
 * pre-connect row: its `cmmdProjectId` comes from the runtime-list resource,
 * which `loadCachedRows()` seeds from localStorage with `stale: true`, so
 * trusting it unread would be the same cached-value-as-removal-target defect
 * that instance 6 was, just entering by a different door. One GET on an action
 * that already budgets 30s per step is the price of that door being shut.
 *
 * A failed catalog read REJECTS rather than returning a plan. Treating an
 * unreadable catalog as "no registration" is the fail-open this whole module
 * exists to prevent — the runtime-routed path succeeds against a live runtime
 * while the registration, and therefore the sidebar row, survives.
 *
 * Deliberately ignores every id a CALLER can pass:
 *  - `options.cmmdProjectId` is the CMMD PLANNING project behind the opt-in
 *    "delete in CMMD as well" checkbox; on a local row it is a cwd link
 *    (`useCmmdProjectLink`) with nothing to do with Forge Cloud.
 *  - `options.cloudRegistrationProjectId` is what some caller BELIEVES this row
 *    to be. Instance 5 accepted it as proof; instance 6 accepted it as a
 *    tie-breaker over the catalog's own answer and deleted a stranger. It is
 *    now read nowhere at all: the catalog decides, or nothing happens.
 *
 * THE UNMATCHABLE-ROW BLIND SPOT (instances 3 and 4) is closed by `locate`
 * rather than here. A connected row can only be matched by an overlay locator,
 * and a catalog row bound to a DIFFERENT runtime or Environment id is exactly
 * as unmatchable as one with `runtime: null` — one field value away, which is why an
 * earlier fix that special-cased the null shape did not hold. `locate` can only
 * answer "absent" when every row is positively ruled out, so both shapes come
 * back `unprovable` by construction rather than by a predicate someone has to
 * keep widening.
 *
 * A connected row that the authoritative sidebar catalog matched by stable
 * project/repository identity carries `cloudRegistrationProjectId`. That is
 * row-owned identity, not a caller hint: the live catalog must still locate it
 * and mint the removal target. Rows without that proof continue to fail closed.
 */
async function resolveCloudRemovalPlan(project: RemovableForgeProject): Promise<CloudRemovalPlan> {
  const routes = resolveCloudRemovalRoutes(project);
  const catalogRoute = routes[0];
  if (!catalogRoute || !removalReadsCloudCatalog(project)) return planNoCloudRegistration();

  const ownRegistrationProjectId =
    typeof project.preConnectCloud?.cmmdProjectId === "number"
      ? project.preConnectCloud.cmmdProjectId
      : typeof project.cloudRegistrationProjectId === "number"
        ? project.cloudRegistrationProjectId
        : undefined;

  const catalog: CloudProjectCatalog = await withRemoveProjectOperationTimeout(
    REMOVAL_STEP_LABELS.readCatalog,
    (signal) => readLiveCloudProjectCatalog(signal),
  );

  return planCloudRemovalLookups(
    lookupCloudRemovalRoutes(catalog, routes, ownRegistrationProjectId),
  );
}

function unavailableCloudCheckoutPreview(
  projectId: string,
  blocker: string,
): ProjectCheckoutDeletionPreview {
  return {
    projectId,
    canonicalPath: null,
    method: "unavailable",
    dirty: false,
    blockers: [blocker],
    fingerprint: null,
  };
}

/**
 * Preview one cloud checkout through the same live-catalog identity resolver
 * used by removal. The returned token is bound by CMMD to that registration,
 * guest path, and filesystem fingerprint.
 */
export async function previewSidebarProjectCheckoutDeletion(
  project: RemovableForgeProject,
): Promise<ProjectCheckoutDeletionPreview> {
  const plan = await resolveCloudRemovalPlan(project);
  return plan.match<Promise<ProjectCheckoutDeletionPreview>>({
    refuse: (reason) =>
      Promise.resolve(
        unavailableCloudCheckoutPreview(
          project.id,
          `Forge Cloud could not identify this checkout. ${reason}`,
        ),
      ),
    noCloudRegistration: () =>
      Promise.resolve(
        unavailableCloudCheckoutPreview(project.id, "No Forge Cloud registration was found."),
      ),
    deleteRegistrations: async (registrations) => {
      const registration = registrations[0];
      if (!registration || registrations.length !== 1) {
        return unavailableCloudCheckoutPreview(
          project.id,
          "Forge Cloud could not isolate one checkout for deletion.",
        );
      }
      const bootstrap = readDesktopCloudBootstrap();
      const preview = await createCmmdControlPlaneClient(bootstrap).previewCloudProjectRemoval({
        projectId: registration.target.registrationProjectId,
      });
      return {
        projectId: project.id,
        canonicalPath: preview.workspaceRoot,
        method: "permanent",
        dirty: preview.git.dirty,
        blockers: preview.blockers,
        fingerprint: preview.previewToken,
      };
    },
  });
}

export interface RemoveSidebarProjectOptions {
  /**
   * What the caller believes this row's Forge Cloud REGISTRATION id to be.
   *
   * ACCEPTED AND IGNORED, on purpose. It is still in the signature so the
   * existing call sites and their regression tests keep compiling, and so this
   * doc block sits where anyone tempted to wire it back up will read it — but
   * `resolveCloudRemovalPlan` does not take options at all, and this value is
   * referenced nowhere else in this module.
   *
   * It is ignored because it cannot answer the question that matters. A number
   * from a caller can establish "the catalog contains a row with this id"; it
   * can never establish "this is the row the user clicked". Instance 5 took it
   * as proof of absence; instance 6 let it win a `??` over the catalog's own
   * answer and started removal for an unrelated registration while the
   * clicked project stayed on screen. Its real provenance is the runtime-list
   * snapshot, which `loadCachedRows()` seeds from localStorage.
   *
   * Wiring it back in would not merely break the rule — the resolver takes a
   * `CloudRemovalTarget`, and a `number` cannot be turned into one.
   */
  readonly cloudRegistrationProjectId?: number | null;
  /**
   * Called with the label of each step as that step BEGINS.
   *
   * Purely observational — it can neither choose a branch nor cancel one, and
   * omitting it changes nothing about what removal does. That is deliberate in
   * a module whose entire history is callers being allowed to influence what
   * gets deleted: a reporter that could steer would be the seventh instance of
   * that bug.
   *
   * It exists because the caller has to say something true while the durable
   * saga is in flight. The same labels are used for persisted phases and
   * bounded browser operations, so visible progress and failures agree.
   */
  readonly onProgress?: (step: RemovalStepLabel) => void;
  /**
   * Whether removing the project also ends the Environment hosting it.
   *
   * Defaults to `false`. It was `true` while the sidebar's dialog still promised
   * runtime teardown in its copy, because changing behaviour under unchanged
   * copy is how a destructive action starts lying. That copy no longer promises
   * it, so the reason expired.
   *
   * `false` is the right default on its own terms too. Every caller in the tree
   * names its intent, which leaves the default for one that has not considered
   * the question — and an Environment hosts many Projects, so ending it for a
   * caller that never asked takes down Projects nobody chose.
   */
  readonly destroyRuntime?: boolean;
  /**
   * CMMD-issued authority to delete the selected cloud checkout before its
   * durable registration removal. Omission preserves files.
   */
  readonly checkoutDeletion?: {
    readonly previewToken: string;
    readonly forceDirty: boolean;
  };
}

/**
 * Tear down the runtime-side state behind a project row: destroy the machine
 * and drop the runtime's own local project/thread state.
 *
 * Both calls travel to (or about) the runtime, so both are unavailable exactly
 * when it is dead. They are run CONCURRENTLY — in series each one paid its own
 * 30s timeout, so a dead runtime froze the confirm dialog for ~60s.
 *
 * Never rejects: it reports whether the runtime-routed `project.delete`
 * authoritatively removed the project, plus whatever failed along the way.
 */
async function teardownProjectRuntimeState(
  project: RemovableForgeProject,
  destroyRuntime: boolean,
): Promise<{ readonly removed: boolean; readonly errors: readonly unknown[] }> {
  const errors: unknown[] = [];

  if (project.preConnectCloud) {
    // A caller that is not ending the Environment has nothing to do on this
    // branch: the registration is what goes, and the machine -- which may not
    // even exist yet -- is not ours to touch. Without this the flag would be
    // accepted here and ignored, which is worse than not offering it.
    if (!destroyRuntime) return { removed: true, errors };

    // Nothing is provisioned yet, so there is no runtime to dispatch into.
    const teardown = await settleRemovalStep("Destroying the cloud runtime", (signal) =>
      destroyCmmdRuntimeForEnvironment(project.environmentId, { signal }),
    );
    if (!teardown.ok) errors.push(teardown.error);
    return { removed: teardown.ok, errors };
  }

  const memberRefs = dedupeProjectRefs([
    scopeProjectRef(project.environmentId, project.id),
    ...project.memberProjectRefs,
  ]);
  return removeProjectMembers(memberRefs, { destroyRuntime });
}

/**
 * Remove a project row from the sidebar.
 *
 * The authoritative removal path depends on what kind of row this is:
 *
 *  - FORGE CLOUD row (pre-connect or connected): POST creates a durable CMMD
 *    removal receipt that unregisters the project and removes its Forge state.
 *    The Environment remains intact. A worker resumes the same receipt after
 *    process/browser failure. The UI
 *    polls with a finite budget and trusts terminal receipt/catalog state, not
 *    a provider request held open in the browser.
 *  - PLAIN LOCAL/desktop row without cloud members: there is no control-plane
 *    row, so the runtime-routed `project.delete` IS the removal and its failure
 *    must surface. Such a row never issues the cloud unregister, even when it
 *    carries a cwd-based CMMD planning-project link. A mixed local+cloud row
 *    completes the durable cloud saga first, then performs bounded deletion of
 *    each local member so the logical row cannot leave local state behind.
 *
 * Local-only teardown remains bounded and reports deletion failures. Cloud
 * project removal is never fire-and-forget browser work. CMMD owns its
 * persisted registration-removal saga and retries after a recorded failure.
 *
 * Throws when the authoritative step did not succeed, so the caller surfaces
 * the failure instead of closing the dialog over a no-op.
 *
 * WHICH BRANCH IS TAKEN IS NOT DECIDED HERE. `resolveCloudRemovalPlan` decides
 * it once, from the row alone, and hands back a plan whose only member is
 * `match`. There is no id to test for `undefined`, no flag to forget and no
 * `??` to write: the three branches below are exhaustive because the compiler
 * says so, and the destructive one is the only one that receives a target.
 */
export async function removeSidebarProject(
  project: RemovableForgeProject,
  options: RemoveSidebarProjectOptions = {},
): Promise<void> {
  const clickedRuntimeId = classifyEnvironmentId(project.environmentId).legacyRuntimeId;
  // Observational only — it names what is happening, it never decides anything.
  // Defaulting to a no-op keeps every branch below free of `?.` noise and keeps
  // the reporter genuinely optional.
  const reportProgress: (step: RemovalStepLabel) => void = options.onProgress ?? (() => {});

  // Rejects when the catalog could not be read at all. That has to surface:
  // silently taking the runtime-routed path instead would leave the
  // registration — and so the sidebar row — behind while reporting success.
  //
  // Announced BEFORE the await, not after: a step reported on completion can
  // only ever describe work the user already waited through, which is precisely
  // the "nothing is happening" experience this reporting exists to end. Guarded
  // by the same predicate the resolver uses, so a local row is never told about
  // a catalog read that will not happen.
  if (removalReadsCloudCatalog(project)) reportProgress(REMOVAL_STEP_LABELS.readCatalog);
  const plan = await resolveCloudRemovalPlan(project);

  await plan.match<Promise<void>>({
    // "Cannot tell" is not "already gone". Acting anyway would either delete an
    // id nothing vouches for or hand the confirming read a question it cannot
    // answer — and in both cases the row is still in the catalog while the
    // dialog closes on a success. Refuse before issuing anything destructive,
    // and in particular do NOT fall through to the runtime-routed path, which
    // succeeds against a live runtime without touching the registration.
    refuse: (reason) =>
      Promise.reject(
        new Error(
          `Forge Cloud could not identify this project's registration, so nothing was removed. Reload the project list and try again. (${reason})`,
        ),
      ),
    // No control-plane row to delete: a local/desktop row, or a cloud row the
    // catalog PROVABLY does not list. The runtime-routed delete is all there is.
    noCloudRegistration: async () => {
      if (options.checkoutDeletion) {
        throw new Error("The Forge Cloud checkout is no longer registered. No files were deleted.");
      }
      // Which step teardown actually awaits depends on the row: a pre-connect
      // row has no runtime to dispatch into, so its only awaited work is the
      // destroy. Reporting the other one would name work that is not running.
      reportProgress(
        project.preConnectCloud
          ? REMOVAL_STEP_LABELS.destroyRuntime
          : REMOVAL_STEP_LABELS.deleteForgeProject,
      );
      const teardown = await teardownProjectRuntimeState(project, options.destroyRuntime ?? false);
      if (!teardown.removed) {
        throw teardown.errors[0] ?? new Error("The project could not be removed.");
      }
    },
    deleteRegistrations: async (registrations) => {
      if (options.checkoutDeletion && registrations.length !== 1) {
        throw new Error("Forge Cloud could not isolate one checkout for deletion.");
      }
      reportProgress(REMOVAL_STEP_LABELS.unregister);
      // `deletePlanningProject` is CMMD's field, not a Forge choice: Forge no
      // longer deletes CMMD projects, so it is always false on the way out.
      // The flag stays on the wire because CMMD's durable receipt carries it
      // and this client still has to parse receipts that set it.
      const operations = await Promise.all(
        registrations.map((registration) =>
          removeCloudRegistrationWithSaga({
            registrationProjectId: registration.target.registrationProjectId,
            deletePlanningProject: false,
            ...(options.checkoutDeletion ? { checkoutDeletion: options.checkoutDeletion } : {}),
            onProgress: reportProgress,
          }),
        ),
      );
      if (
        options.checkoutDeletion &&
        operations.some((operation) => operation.checkoutFilesStatus !== "deleted")
      ) {
        throw new Error("Forge Cloud did not confirm that the checkout files were deleted.");
      }
      // Prove every cloud registration is gone before touching local members.
      // A corrupt completed receipt that left its catalog row behind must not
      // turn a failed cloud removal into a partial local deletion.
      reportProgress(REMOVAL_STEP_LABELS.confirm);
      await confirmSidebarProjectLeftCloudCatalog(registrations);
      // FOR-36359. Unregistering used to end the machine, so the project row on
      // it died with the Sprite and deleting it here would have been work on a
      // host that no longer existed. Runtimes are per-owner since 2026-08-28 and
      // a sibling project keeps the machine alive, so the row now outlives its
      // registration: it survived an app restart and stayed selectable at /new.
      //
      // Best effort on purpose. The registration IS the removal and it is
      // already gone, so a runtime that is stopped, unreachable, or was ended by
      // the unregister has no row left to delete, and failing the whole removal
      // for that would report a completed removal as broken.
      const runtimeMemberRefs = resolveRuntimeHostedProjectMemberRefs(project);
      if (runtimeMemberRefs.length > 0) {
        reportProgress(REMOVAL_STEP_LABELS.deleteForgeProject);
        await removeProjectMembers(runtimeMemberRefs, { destroyRuntime: false });
      }
      const localMemberRefs = resolveLocalProjectMemberRefs(project);
      if (localMemberRefs.length > 0) {
        reportProgress(REMOVAL_STEP_LABELS.deleteForgeProject);
        const localCleanup = await removeProjectMembers(localMemberRefs, {
          destroyRuntime: false,
        });
        if (!localCleanup.removed) {
          throw localCleanup.errors[0] ?? new Error("The local project could not be removed.");
        }
      }
    },
  });

  if (clickedRuntimeId !== null) clearCloudRuntimeProjectHandoff(clickedRuntimeId);

  // Reconcile the sidebar against the new reality. The pre-connect path
  // historically refreshed and the connected path did not, so a removed
  // connected project lingered until the slow SSE/poll interval converged.
  //
  // Reported too, even though it does not block this promise: it is the step
  // between "the delete landed" and "the row is off screen", and leaving it
  // unnamed is what made a completed removal still look unfinished.
  reportProgress(REMOVAL_STEP_LABELS.reconcileSidebar);
  refreshCmmdForgeRuntimeList();
}
